import omit from "lodash/omit";

export function calculateGrossProfit(data: {
  sale_price?: number;
  purchase_price?: number;
  shipping_cost?: number;
  shipping_cost_analytics?: number;
  other_fees?: number;
  transaction_fees?: number;
  extra_shipping_cost?: number;
  extra_transaction_fees?: number;
}): number {
  return (
    (+(data.sale_price || 0) || 0) -
    (+(data.purchase_price || 0) || 0) -
    (+(data.shipping_cost || 0) || 0) -
    (+(data.shipping_cost_analytics || 0) || 0) -
    (+(data.other_fees || 0) || 0) -
    (+(data.transaction_fees || 0) || 0) -
    (+(data.extra_shipping_cost || 0) || 0) -
    (+(data.extra_transaction_fees || 0) || 0)
  );
}

export function calculateNetProfit(data: {
  gross_profit: number;
  estimated_income_tax: number;
  other_business_costs?: number;
}): number {
  return (
    data.gross_profit -
    data.estimated_income_tax -
    (data.other_business_costs || 0)
  );
}

interface FirebaseDate {
  toDate: () => Date;
}

function isFirebaseDate(date: any): date is FirebaseDate {
  return date && (date as FirebaseDate).toDate ? true : false;
}

function toDateMaybe(
  v: undefined | null | Date | FirebaseDate | string | number
): Date | null {
  if (v == null) return null;
  const d = isFirebaseDate(v)
    ? v.toDate()
    : new Date(v as string | number | Date);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Earliest valid date among purchase_date, sale_date, return_date; null if none valid. */
export function computeMinSaleDate(sale: {
  purchase_date?: unknown;
  sale_date?: unknown;
  return_date?: unknown;
}): Date | null {
  const dates = [
    toDateMaybe(sale.purchase_date as any),
    toDateMaybe(sale.sale_date as any),
    toDateMaybe(sale.return_date as any),
  ].filter((x): x is Date => x != null);
  if (dates.length === 0) return null;
  return new Date(Math.min(...dates.map((d) => d.getTime())));
}

/** Latest valid date among purchase_date, sale_date, return_date; null if none valid. */
export function computeMaxSaleDate(sale: {
  purchase_date?: unknown;
  sale_date?: unknown;
  return_date?: unknown;
}): Date | null {
  const dates = [
    toDateMaybe(sale.purchase_date as any),
    toDateMaybe(sale.sale_date as any),
    toDateMaybe(sale.return_date as any),
  ].filter((x): x is Date => x != null);
  if (dates.length === 0) return null;
  return new Date(Math.max(...dates.map((d) => d.getTime())));
}

export function daysOnPlatform(
  saleDate: undefined | null | Date | FirebaseDate | number,
  listDate: undefined | null | Date | FirebaseDate | number
): number | null {
  if (!saleDate || !listDate) return null;

  const sale = isFirebaseDate(saleDate)
    ? saleDate.toDate()
    : new Date(saleDate);
  const list = isFirebaseDate(listDate)
    ? listDate.toDate()
    : new Date(listDate);

  if (isNaN(sale.getTime()) || isNaN(list.getTime())) return null;

  return Math.floor((sale.getTime() - list.getTime()) / (1000 * 60 * 60 * 24));
}

/** Recompute gross_profit, min_date, max_date (and tax fields when rate provided). */
export function finalizeSaleComputedFields<
  T extends {
    sale_date?: Date;
    list_date?: Date;
    sale_price?: number;
    purchase_price?: number;
    other_fees?: number;
    shipping_cost?: number;
    shipping_cost_analytics?: number;
    other_business_costs?: number;
    extra_shipping_cost?: number;
    extra_transaction_fees?: number;
    transaction_fees?: number;
  }
>(sale: T, incomeTaxRate?: number) {
  const data: T & {
    days_on_platform?: number;
    estimated_income_tax: number;
    net_profit?: number | null;
    gross_profit: number;
  } = {
    ...sale,
    sale_price: sale.sale_price || 0,
    purchase_price: sale.purchase_price || 0,
    transaction_fees: sale.transaction_fees || 0,
    other_fees: sale.other_fees || 0,
    shipping_cost: sale.shipping_cost || 0,
    shipping_cost_analytics: sale.shipping_cost_analytics || 0,
    other_business_costs: sale.other_business_costs || 0,
    extra_shipping_cost: sale.extra_shipping_cost || 0,
    extra_transaction_fees: sale.extra_transaction_fees || 0,
    gross_profit: 0,
    estimated_income_tax: 0,
  };

  const dop = daysOnPlatform(data.sale_date, data.list_date);
  if (dop != null) data.days_on_platform = dop;

  data.gross_profit = calculateGrossProfit(data);

  if (incomeTaxRate != null) {
    data.estimated_income_tax = data.gross_profit * incomeTaxRate;
    data.net_profit = calculateNetProfit(data);
  }

  return {
    ...data,
    min_date: computeMinSaleDate(data),
    max_date: computeMaxSaleDate(data),
  };
}

// TODO: really dont know the real typing this was copied from the old SalesMatching/CompleteUpload
export function buildSaleFromInventory(
  inventory: any,
  data: any,
  incomeTaxRate?: number
) {
  const sale: any = {
    ...data,
    ...omit(inventory, [
      "return_id",
      "id",
      "item_title",
      "import_date",
      "import_type",
      "import_platform",
      "file",
      "upload",
    ]), // return_id is from inventory and should be omitted
    unmatched: false,
    unreviewed: false,
    inventoryId: inventory.id,
    id: data.id,
  };

  for (const key of [
    "import_date",
    "import_type",
    "import_platform",
    "file",
    "upload",
  ]) {
    delete sale[key];
    if (key in inventory) sale[`inventory_${key}`] = inventory[key];
  }

  const finalized = finalizeSaleComputedFields(sale, incomeTaxRate) as any;

  finalized.inventory = Object.entries(inventory).reduce((i, [k, v]) => {
    if (
      Array.isArray(v) ||
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean" ||
      v instanceof Date
    )
      i[k] = v;
    else if ((v as any)?.toDate) i[k] = (v as any).toDate();

    return i;
  }, {} as Record<string, string | number | Date | boolean | any[]>);

  for (const key of Object.keys(finalized)) {
    if (finalized[key] === undefined) {
      delete finalized[key];
    }
  }

  return finalized;
}
